import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import _ from 'lodash';
import {
    CreateChancel,
    ToolInfo,
    SelectAutocompleteMultiple,
    Input,
    SwitchCustomized,
    Icd10CodeSelect,
    useIcd10Descriptions,
} from 'components';
import { formActions, fundingSourceActions, httpRequestsOnErrorsActions, httpRequestsOnSuccessActions } from 'store';
import { enumValues, ErrorText, FindLoad, FindSuccessItem, Images, useDiagnosisMatchEnabled, useModal, useSecondaryModal } from 'utils';
import { foundingSourceModalStyle } from './styles';
import { AddServiceDiagnosisCode } from './addServiceDiagnosisCode';
import { noteEnums } from '../../../../schedule/core/modals/apptDetail/constants';
import { formBuilderStatusEnums } from 'pages/forms/constants';

const GET_FORMS_ACTION_TYPE = 'GET_FORMS';
export const FundingSourceServiceAdd = ({ info }) => {
    const { serviceTypes } = useSelector((state) => state.system.services);
    const fsSource = useSelector((state) => state.fundingSource.fundingSourceItem);
    const forms = useSelector((state) => state.forms.forms)?.data;
    const formsLoading = FindLoad(GET_FORMS_ACTION_TYPE);
    const ACTION_TYPE = !!info ? 'EDIT_FUNDING_SOURCE_SERVICE' : 'CREATE_FUNDING_SOURCE_SERVICE_BY_ID';
    const classes = foundingSourceModalStyle();
    const dispatch = useDispatch();
    const success = FindSuccessItem(ACTION_TYPE);
    const loader = FindLoad(ACTION_TYPE);
    const location = useLocation();
    const pushInfo = location?.state;
    const { close } = useModal();
    const { openSecondary } = useSecondaryModal();
    const diagnosisMatchEnabled = useDiagnosisMatchEnabled();
    const { handleSubmit, control, clearErrors, setValue, getValues, watch, reset } = useForm({
        trim: true,
        defaultValues: {
            diagnosticCodes: [],
            noteTemplates: [],
        },
    });
    const currentService = watch('serviceTypeId');
    const diagnosticCodes = watch('diagnosticCodes');
    const { descriptions: codeDescriptions, remember: rememberCode } = useIcd10Descriptions(diagnosticCodes || []);
    // Codes typed in from "Add Diagnosis Code" — extra options in the code picker
    const [customCodeOptions, setCustomCodeOptions] = useState([]);
    const selectedNoteTemplates = watch('noteTemplates') || [];

    useEffect(() => {
        dispatch(formActions.getForms({ model: noteEnums?.CLINICAL_NOTES, status: formBuilderStatusEnums.PUBLISHED }));
        return () => {
            clearErrors();
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
        };
    }, []);

    useEffect(() => {
        if (success) {
            close();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
        }
    }, [success]);

    useEffect(() => {
        if (info) {
            const result = {
                ...info,
            };
            reset(result);
        }
    }, [info]);

    // The options list holds only PUBLISHED Session Note templates. A service can still point at
    // templates that were later unpublished; keep them selectable (and visible) so the user sees
    // what is actually stored.
    const savedTemplates = useMemo(() => info?.noteTemplates || [], [info]);
    const noteTemplateOptions = useMemo(() => {
        const list = forms || [];
        const stillLoading = !!formsLoading?.length;
        const missing = savedTemplates
            .filter((saved) => !list.some((form) => form?.id === saved?.id))
            .map((saved) => {
                const name = saved?.title || 'Unavailable template';
                return { id: saved?.id, title: stillLoading ? name : `${name} (unpublished)` };
            });
        return [...list, ...missing];
    }, [forms, savedTemplates, formsLoading?.length]);

    function handleCreate(data) {
        const serviceIdKey = !!info ? 'globServiceId' : 'serviceTypeId';
        const result = _.omitBy(
            data,
            (value) =>
                typeof value === 'undefined' ||
                (typeof value === 'string' && !value?.trim()) ||
                (typeof value === 'object' && _.isEmpty(value))
        );

        const noteTemplateIds = (data?.noteTemplates || []).map((template) => template?.id || template?._id);
        const sandedData = {
            ...result,
            unitSize: +result.unitSize,
            min: +result.min,
            max: +result.max,
            rate: 0,
            name: result?.serviceTypeId?.name,
            [serviceIdKey]: result?.serviceTypeId?.id || result?.serviceTypeId?._id,
            noteTemplateIds,
            onlyUseTemplate: !!data?.onlyUseTemplate,
            diagnosticCodes: data.diagnosticCodes || [],
        };
        delete sandedData.noteTemplates;
        // On edit, send the templates only when the user changed them: the API leaves an omitted
        // `noteTemplateIds` untouched. A linked template that was since deleted is not returned at
        // all, so echoing the list back would erase that link on a no-change save.
        const savedIds = savedTemplates.map((template) => template?.id);
        if (info && _.isEqual([...noteTemplateIds].sort(), [...savedIds].sort())) delete sandedData.noteTemplateIds;

        const sendInfo = {
            ...pushInfo,
        };
        delete sendInfo.page;
        delete sendInfo.activeTab;
        delete sendInfo.limit;
        delete sendInfo.skip;

        if (!!info) {
            dispatch(fundingSourceActions.editFoundingSourceServiceById(info?.id, sandedData, sendInfo));
        } else {
            dispatch(fundingSourceActions.createFoundingSourceServiceById(fsSource?.id, sandedData, sendInfo));
        }
    }

    // Codes come from the ICD-10-CM picker, plus any typed in with "Add Diagnosis Code".
    // Reads the live list (not the render's `diagnosticCodes`): the secondary modal's `onAdd` is a closure from when it opened.
    const handleAddCode = (option) => {
        const current = getValues('diagnosticCodes') || [];
        if (option && !current.includes(option.code)) {
            rememberCode(option);
            setValue('diagnosticCodes', [...current, option.code]);
        }
    };

    // A typed-in code is selected straight away (a chip) and stays in the picker's options.
    const openAddCustomCode = (typed) =>
        openSecondary(
            <AddServiceDiagnosisCode
                defaultCode={typed}
                existingCodes={[...diagnosticCodes, ...customCodeOptions.map((option) => option.code)]}
                onAdd={(option) => {
                    setCustomCodeOptions((prev) => [...prev, option]);
                    handleAddCode(option);
                }}
            />
        );

    const handleRemoveCode = (code) => {
        const remaining = diagnosticCodes.filter((item) => item !== code);
        setValue('diagnosticCodes', remaining);
        // The toggle is disabled without codes — don't leave it (and save it) switched on underneath.
        if (!remaining.length) setValue('requireDiagnosisMatch', false);
    };

    return (
        <div style={{ width: 820 }}>
            <p className="modal-header-title">{info ? 'Edit Service' : 'Add a New Service'}</p>
            <form onSubmit={handleSubmit(handleCreate)}>
                <div className="modal-body-wrapper">
                    <div className="flex-align-start" style={{ gap: 16 }}>
                        <div className="full-width">
                            <SelectAutocompleteMultiple
                                defaultSelected={watch('serviceTypeId') && { name: watch('serviceTypeId')?.name }}
                                options={serviceTypes}
                                label={'Service*'}
                                name="serviceTypeId"
                                control={control}
                                rules={{
                                    required: 'Please select at least one service.',
                                }}
                                renderValue={(option) => option?.name}
                                fullValue={true}
                            />

                            <div className={classes.displayCodeBlock} style={{ height: 'fit-content', padding: '12px 16px' }}>
                                <p className={classes.displayCodeBlockText}>
                                    Display Code:{' '}
                                    <span className={classes.displayCode}>
                                        {currentService?.displayCode ? currentService?.displayCode : '--'}
                                    </span>
                                </p>
                                <p className={classes.displayCodeBlockText} style={{ marginTop: 4 }}>
                                    Category:{' '}
                                    <span className={classes.displayCode}>
                                        {currentService?.category ? currentService?.category : '--'}
                                    </span>
                                </p>
                            </div>
                            <Input
                                name="cptCode"
                                label="CPT Code*"
                                variant="outlined"
                                control={control}
                                maxLength={15}
                                rules={{
                                    maxLength: {
                                        value: 15,
                                        message: 'Max length is 15 digits.',
                                    },
                                    required: 'CPT Code is required.',
                                    validate: (value) => {
                                        return !!value.trim() || 'CPT Code is required';
                                    },
                                }}
                            />
                            <SelectAutocompleteMultiple
                                loadType={GET_FORMS_ACTION_TYPE}
                                options={noteTemplateOptions}
                                label={'Note Templates'}
                                placeholder={'Select Note Templates'}
                                name="noteTemplates"
                                control={control}
                                renderValue={(option) => option?.title}
                                multiple={true}
                            />
                            {!!selectedNoteTemplates.length && (
                                <div
                                    className={classes.requireWrapper}
                                    style={{ marginBottom: 16, borderBottom: '1px dashed #E4E7EC', paddingBottom: 12 }}
                                >
                                    <div style={{marginRight: 12, marginTop: 3}}>
                                    <ToolInfo
                                        text={
                                            'The first template is selected by default on new session notes. If this toggle is on, staff can only use the selected templates.'
                                        }
                                    />
                                    </div>
                                    <SwitchCustomized name="onlyUseTemplate" control={control} />
                                    <p className={classes.requireText}>
                                        {selectedNoteTemplates.length > 1 ? 'Only use these templates' : 'Only use this template'}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="full-width">
                            <SelectAutocompleteMultiple
                                options={enumValues.FUNDING_MODIFIER_SERVICE_TYPES}
                                label={'Type'}
                                name={'type'}
                                control={control}
                            />
                            <Input
                                variant={'outlined'}
                                type={'number'}
                                control={control}
                                label={'Unit Size (Minutes)*'}
                                name={'unitSize'}
                                maxLength={3}
                                rules={{
                                    required: 'Unit size is required',
                                    maxLength: {
                                        value: 3,
                                        message: 'Max length is 3',
                                    },
                                }}
                            />
                            <div className={classes.foundingSourceModalsBodyBlock} style={{ gap: '16px' }}>
                                <Input
                                    name={'min'}
                                    variant={'outlined'}
                                    type={'number'}
                                    label={'Min Unit*'}
                                    control={control}
                                    rules={{
                                        required: 'Min unit is required',
                                        validate: {
                                            positive: (v) => parseInt(v) > 0 || 'Should be greater than 0',
                                            validateNumber: (_, formValues) => {
                                                if (formValues.max)
                                                    return (
                                                        +formValues.min < +formValues.max || ErrorText.equalityError('Min Unit', 'Max Unit')
                                                    );
                                            },
                                        },
                                    }}
                                />
                                <Input
                                    name={'max'}
                                    variant={'outlined'}
                                    type={'number'}
                                    label={'Max Unit*'}
                                    control={control}
                                    rules={{
                                        required: 'Max unit is required',
                                        validate: {
                                            positive: (v) => parseInt(v) > 0 || 'Should be greater than 0',
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="diagnostic-code-section">
                        <div className="diagnostic-codes-head">
                            <p className="diagnostic-codes-title">Diagnostic Codes</p>
                            {diagnosisMatchEnabled && (
                                <p className="diagnostic-codes-subtitle">
                                    Codes this service accepts. They are never copied to every client - each appt uses the client's own
                                    codes that appear in this list.
                                </p>
                            )}
                            {!!diagnosticCodes?.length && (
                                <>
                                    <p className="diagnostic-codes-label">Accepted diagnosis codes</p>
                                    <div className="diagnostic-code-chips">
                                        {diagnosticCodes.map((code) => (
                                            <div key={code} className="diagnostic-code-chip">
                                                <p>
                                                    <b>{code}</b>
                                                    {codeDescriptions[code] && ` ${codeDescriptions[code]}`}
                                                </p>
                                                <button type="button" aria-label={`Remove ${code}`} onClick={() => handleRemoveCode(code)}>
                                                    <img src={Images.closeChipBlue} alt="" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="diagnostic-codes-picker">
                            <Icd10CodeSelect
                                label="Add Code"
                                placeholder="Search ICD-10-CM by code or description"
                                exclude={diagnosticCodes}
                                onSelect={handleAddCode}
                                onAddCustom={openAddCustomCode}
                                customOptions={customCodeOptions}
                                clearOnSelect
                                noError
                            />
                        </div>

                        {/* Diagnosis matching (org switch on): enforce the list against the client's diagnoses */}
                        {diagnosisMatchEnabled && (
                            <div className="diagnostic-codes-require">
                                <div className="diagnostic-codes-require-text">
                                    <p className="diagnostic-codes-require-title">Require all diagnoses to match</p>
                                    <p className="diagnostic-codes-require-desc">Block billing when the client has none of these codes.</p>
                                </div>
                                <SwitchCustomized
                                    name="requireDiagnosisMatch"
                                    control={control}
                                    checkedBgColor={'#0C66E4'}
                                    disabled={!diagnosticCodes?.length}
                                />
                            </div>
                        )}
                    </div>

                    <div className={classes.switchesWrapper}>
                        <div className={classes.requireWrapper}>
                            <ToolInfo
                                text={
                                    'If this toggle is on, all appointments that use this service will by default require Client/Guardian signature upload before rendering the session'
                                }
                            />
                            <p className={classes.requireText}>Client/Guardian Signature Req.</p>
                            <SwitchCustomized name="requireSignature" control={control} />
                        </div>
                        <div className={classes.requireWrapper}>
                            <ToolInfo
                                text={
                                    'If this toggle is on, all appointments that use this service will by default require Provider signature upload before rendering the session'
                                }
                            />
                            <p className={classes.requireText}>Provider Signature Req.</p>
                            <SwitchCustomized name="requireProviderSignature" control={control} />
                        </div>
                        <div className={classes.requireWrapper}>
                            <ToolInfo
                                text={
                                    'If this toggle is on, all appointments that use this service will by default require at least one session note before rendering the session'
                                }
                            />
                            <p className={classes.requireText}>Require Notes</p>
                            <SwitchCustomized name="requireNotes" control={control} />
                        </div>
                        {info?.id && (
                            <div className={classes.requireWrapper}>
                                <ToolInfo
                                    text={
                                        !watch('active')
                                            ? 'Inactive services cannot be used to create new appointments.'
                                            : 'Active services can be used to create new appointments.'
                                    }
                                />
                                <p className={classes.requireText}>Is Active</p>
                                <SwitchCustomized name="active" control={control} />
                            </div>
                        )}
                    </div>
                </div>
                <CreateChancel
                    type="submit"
                    loader={!!loader.length}
                    create={info ? 'Save' : 'Add'}
                    chancel={'Cancel'}
                    onClose={close}
                    buttonWidth={'100%'}
                />
            </form>
        </div>
    );
};
